import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, 
  Users,
  Mail,
  Phone,
  DollarSign,
  Trash2,
  Edit,
  Calendar,
  Wrench,
  Sparkles
} from 'lucide-react';
import { getWorkers, createWorker, updateWorker, deleteWorker, getWorkerSchedule } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { format, parseISO } from 'date-fns';

const SKILLS = [
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'maintenance', label: 'General Maintenance' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'general', label: 'General' },
];

export const WorkersView = ({ onRefresh }) => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerSchedule, setWorkerSchedule] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    hourly_rate: '',
    skills: [],
    notes: '',
  });

  const fetchWorkers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getWorkers();
      setWorkers(response.data);
    } catch (error) {
      console.error('Error fetching workers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const data = {
        ...formData,
        hourly_rate: parseFloat(formData.hourly_rate) || 0,
      };

      if (isEditing && selectedWorker) {
        await updateWorker(selectedWorker.id, data);
        toast.success('Worker updated');
      } else {
        await createWorker(data);
        toast.success('Worker created');
      }
      
      resetForm();
      setDialogOpen(false);
      fetchWorkers();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to save worker');
    }
  };

  const handleDelete = async () => {
    if (!selectedWorker) return;
    
    try {
      await deleteWorker(selectedWorker.id);
      toast.success('Worker deleted');
      setDeleteDialogOpen(false);
      setSelectedWorker(null);
      fetchWorkers();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to delete worker');
    }
  };

  const handleViewSchedule = async (worker) => {
    try {
      const response = await getWorkerSchedule(worker.id);
      setWorkerSchedule(response.data);
      setSelectedWorker(worker);
      setScheduleDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load schedule');
    }
  };

  const openEditDialog = (worker) => {
    setFormData({
      name: worker.name,
      email: worker.email,
      phone: worker.phone || '',
      hourly_rate: worker.hourly_rate?.toString() || '',
      skills: worker.skills || [],
      notes: worker.notes || '',
    });
    setSelectedWorker(worker);
    setIsEditing(true);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      hourly_rate: '',
      skills: [],
      notes: '',
    });
    setSelectedWorker(null);
    setIsEditing(false);
  };

  const toggleSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  return (
    <div className="space-y-6" data-testid="workers-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Workers
          </h1>
          <p className="text-slate-500 mt-1">
            Manage your cleaning and maintenance staff
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="add-worker-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Worker
        </Button>
      </div>

      {/* Workers Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : workers.length === 0 ? (
        <Card className="bg-white border border-slate-100">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No workers yet</h3>
            <p className="text-slate-500 mb-4">Add workers to assign them to tasks</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Worker
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {workers.map((worker) => (
            <Card 
              key={worker.id}
              className={`bg-white border card-hover ${!worker.is_active ? 'opacity-60' : 'border-slate-100'}`}
              data-testid={`worker-card-${worker.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-sm font-bold text-slate-600">
                        {worker.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{worker.name}</h3>
                      {!worker.is_active && (
                        <span className="text-xs text-red-500">Inactive</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewSchedule(worker)}
                      data-testid={`schedule-btn-${worker.id}`}
                    >
                      <Calendar className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(worker)}
                      data-testid={`edit-worker-${worker.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedWorker(worker); setDeleteDialogOpen(true); }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-worker-${worker.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2 text-slate-600">
                    <Mail className="h-4 w-4" />
                    {worker.email}
                  </p>
                  {worker.phone && (
                    <p className="flex items-center gap-2 text-slate-600">
                      <Phone className="h-4 w-4" />
                      {worker.phone}
                    </p>
                  )}
                  <p className="flex items-center gap-2 text-emerald-600 font-medium">
                    <DollarSign className="h-4 w-4" />
                    ${worker.hourly_rate}/hr
                  </p>
                </div>

                {worker.skills && worker.skills.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {worker.skills.map(skill => (
                      <span 
                        key={skill}
                        className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Worker Dialog */}
      <Dialog open={dialogOpen} onOpenChange={() => { resetForm(); setDialogOpen(false); }}>
        <DialogContent className="sm:max-w-[450px]" data-testid="worker-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              {isEditing ? 'Edit Worker' : 'Add New Worker'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Smith"
                data-testid="worker-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                  data-testid="worker-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="555-1234"
                  data-testid="worker-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
              <Input
                id="hourly_rate"
                type="number"
                min="0"
                step="0.01"
                value={formData.hourly_rate}
                onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                placeholder="25.00"
                data-testid="worker-rate-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Skills</Label>
              <div className="grid grid-cols-2 gap-2">
                {SKILLS.map(skill => (
                  <div key={skill.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`skill-${skill.value}`}
                      checked={formData.skills.includes(skill.value)}
                      onCheckedChange={() => toggleSkill(skill.value)}
                    />
                    <label htmlFor={`skill-${skill.value}`} className="text-sm">
                      {skill.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { resetForm(); setDialogOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="worker-submit-btn">
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[80vh] overflow-y-auto" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              Schedule - {selectedWorker?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {workerSchedule?.schedule?.length > 0 ? (
              <div className="space-y-2">
                {workerSchedule.schedule.map((item) => (
                  <div 
                    key={item.task?.id || `${item.type}-${item.task?.scheduled_date}`}
                    className={`p-3 rounded-lg border ${
                      item.type === 'cleaning' 
                        ? 'bg-emerald-50 border-emerald-200' 
                        : 'bg-amber-50 border-amber-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {item.type === 'cleaning' 
                        ? <Sparkles className="h-4 w-4 text-emerald-600" />
                        : <Wrench className="h-4 w-4 text-amber-600" />
                      }
                      <span className="font-medium capitalize">{item.type}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        item.task.status === 'completed' 
                          ? 'bg-emerald-100 text-emerald-700'
                          : item.task.status === 'in_progress'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {item.task.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">
                      {format(parseISO(item.task.scheduled_date), 'MMM d, yyyy')}
                    </p>
                    {item.task.title && (
                      <p className="text-sm text-slate-800 mt-1">{item.task.title}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">
                No tasks assigned to this worker.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Worker</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedWorker?.name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WorkersView;
